import {
  AwardIcon,
  BanknoteIcon,
  BarChart3Icon,
  BellIcon,
  CalendarDaysIcon,
  ChartPieIcon,
  ClipboardListIcon,
  CreditCardIcon,
  DatabaseIcon,
  DoorOpenIcon,
  GiftIcon,
  MailIcon,
  MapPinIcon,
  MegaphoneIcon,
  MessageSquareTextIcon,
  MonitorIcon,
  NotebookPenIcon,
  PackageIcon,
  PaletteIcon,
  ReceiptIcon,
  ShoppingBagIcon,
  UsersIcon,
  UsersRoundIcon,
} from "lucide-react";

// Feature groups on /features. Same groups and links as the header's
// Features menu, with a short blurb and link label for each card.
export const FEATURE_GROUPS = [
  {
    id: "client-experience",
    label: "Client experience",
    lead: "Delight",
    accent: "your clients",
    intro: "Give every client a smooth path from the first booking to the next visit.",
    items: [
      {
        title: "Online booking",
        description:
          "Let clients book, pay a deposit, and rebook 24/7 from your website, Google, and social media.",
        cta: "Boost bookings",
        href: "/features/online-booking",
        icon: ShoppingBagIcon,
      },
      {
        title: "Client profiles",
        description:
          "Keep notes, formulas, photos, and visit history together so every visit feels personal.",
        cta: "Connect with clients",
        href: "/features/client-management",
        icon: UsersRoundIcon,
      },
      {
        title: "Automated notifications",
        description:
          "Reminders, booking updates, receipts, and birthday messages go out by email and text on their own.",
        cta: "Automate updates",
        href: "/features/notifications",
        icon: BellIcon,
      },
    ],
  },
  {
    id: "run-your-business",
    label: "Run your business",
    lead: "Run",
    accent: "your business",
    intro: "Everything your front desk and team need, in one place that works on any device.",
    items: [
      {
        title: "Calendar & scheduling",
        description:
          "Set custom availability, buffer and processing times, and see your whole team's day at a glance.",
        cta: "Optimize my calendar",
        href: "/features/booking-scheduling",
        icon: CalendarDaysIcon,
      },
      {
        title: "Team management",
        description:
          "Give each team member their own login and permissions, and track commissions and tips automatically.",
        cta: "Manage your team",
        href: "/features/staff-management",
        icon: UsersIcon,
      },
      {
        title: "Reports & insights",
        description:
          "A live performance dashboard plus sales, client, and product reports you can export in seconds.",
        cta: "Explore reports",
        href: "/features/reports",
        icon: ChartPieIcon,
      },
      {
        title: "Intake forms",
        description:
          "Build custom forms and waivers, send them at booking, and keep signed copies on each client's profile.",
        cta: "Go paperless",
        href: "/features/intake-forms",
        icon: ClipboardListIcon,
      },
      {
        title: "SOAP notes",
        description:
          "Write treatment notes from your own templates, add marked-up images, then sign and lock them.",
        cta: "Take better notes",
        href: "/features/soap-notes",
        icon: NotebookPenIcon,
      },
      {
        title: "Payroll",
        description:
          "Turn tracked hours, tips, and commissions into paychecks with one click through Gusto.",
        cta: "Simplify payroll",
        href: "/features/payroll",
        icon: BanknoteIcon,
      },
      {
        title: "Free data transfer",
        description:
          "Switching systems? We move your clients, services, and products to Zobliss at no cost.",
        cta: "Make the switch",
        href: "/features/data-transfer",
        icon: DatabaseIcon,
      },
    ],
  },
  {
    id: "payments",
    label: "Payments",
    lead: "Simplify",
    accent: "payments",
    intro: "Fast checkout in the chair, at the desk, or online, with clear numbers behind every sale.",
    items: [
      {
        title: "Payments",
        description:
          "Take secure card and tap-to-pay payments online and in-store, with tips built into checkout.",
        cta: "Streamline payments",
        href: "/features/payments",
        icon: CreditCardIcon,
      },
      {
        title: "POS & kiosk mode",
        description:
          "Check clients out and take payment right from the calendar on one touchscreen.",
        cta: "See the kiosk",
        href: "/features/pos-kiosk",
        icon: MonitorIcon,
      },
      {
        title: "Invoices",
        description:
          "Create invoices in seconds, let clients pay from anywhere, and track every one at a glance.",
        cta: "Get paid faster",
        href: "/features/invoices",
        icon: ReceiptIcon,
      },
      {
        title: "Sales analytics",
        description:
          "Track daily revenue, top services, and team performance, then export tax-ready reports.",
        cta: "Track your sales",
        href: "/features/sales",
        icon: BarChart3Icon,
      },
    ],
  },
  {
    id: "grow-your-business",
    label: "Grow your business",
    lead: "Grow",
    accent: "your revenue",
    intro: "Add recurring income, sell more retail, and expand to new locations without new software.",
    items: [
      {
        title: "Memberships & packages",
        description:
          "Sell recurring memberships and prepaid service packages with automatic billing and renewals.",
        cta: "Build loyalty",
        href: "/features/memberships",
        icon: AwardIcon,
      },
      {
        title: "Gift cards",
        description:
          "Sell branded e-gift cards online and in-store, and track every balance at checkout.",
        cta: "Sell gift cards",
        href: "/features/gift-cards",
        icon: GiftIcon,
      },
      {
        title: "Inventory & retail",
        description:
          "Track stock, get low-stock alerts, and see retail sales and profit in one report.",
        cta: "Manage inventory",
        href: "/features/inventory-retail-management",
        icon: PackageIcon,
      },
      {
        title: "Multi-location",
        description:
          "Run every location from one account with location-aware teams, schedules, and reporting.",
        cta: "Add locations",
        href: "/features/multi-location",
        icon: MapPinIcon,
      },
      {
        title: "Rooms & resources",
        description:
          "Book rooms, chairs, and equipment with every appointment and never double-book them.",
        cta: "Plan your space",
        href: "/features/resource-management",
        icon: DoorOpenIcon,
      },
    ],
  },
  {
    id: "build-your-brand",
    label: "Build your brand",
    lead: "Build",
    accent: "your brand",
    intro: "Reach the right clients at the right time and look polished everywhere they find you.",
    items: [
      {
        title: "Marketing & promotions",
        description:
          "Fill empty slots with smart campaigns, win-back messages, and birthday rewards.",
        cta: "Simplify marketing",
        href: "/features/marketing",
        icon: MegaphoneIcon,
      },
      {
        title: "Email marketing",
        description:
          "Send good-looking campaigns from reusable templates to exactly the clients you choose.",
        cta: "Send a campaign",
        href: "/features/email-marketing",
        icon: MailIcon,
      },
      {
        title: "Text marketing",
        description:
          "Reach clients instantly with SMS campaigns for flash offers, open slots, and news.",
        cta: "Start texting",
        href: "/features/text-marketing",
        icon: MessageSquareTextIcon,
      },
      {
        title: "Custom branding & design",
        description:
          "A booking site, gift cards, and social assets designed to match your brand.",
        cta: "Design your brand",
        href: "/features/branding",
        icon: PaletteIcon,
      },
    ],
  },
];
